import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  getSPDataWithParams,
  getSPDataWithoutParams,
  patchSPDataWithParams,
  postSPDataWithoutParams,
  postSPDataWithParams,
} from './services'

function getResponseMessage(response, fallbackMessage) {
  return (
    response?.data?.message ||
    response?.response?.data?.message ||
    response?.message ||
    fallbackMessage
  )
}

export async function getPractitionerOnboardingBootstrap() {
  const response = await getSPDataWithoutParams('/api/secure/cc/support/practitioner-onboarding/bootstrap')

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboardingBootstrap || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner onboarding references.'),
    rawResponse: response,
  }
}

export async function getPractitionerOnboardingDetail(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/practitioner-onboarding/detail', {
    ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
    ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboarding || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner onboarding status.'),
    rawResponse: response,
  }
}

export async function getPractitionerAuditBootstrap() {
  const response = await getSPDataWithoutParams('/api/secure/cc/support/practitioner-audit/bootstrap')

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboardingBootstrap || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner audit references.'),
    rawResponse: response,
  }
}

export async function getPractitionerAuditDetail(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/practitioner-audit/detail', {
    ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
    ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboarding || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner audit status.'),
    rawResponse: response,
  }
}

export async function getPractitionerAuditRooms(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/practitioner-audit/rooms', {
    ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
    ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerAuditRooms || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: [],
    message: getResponseMessage(response, 'Unable to load practitioner audit rooms.'),
    rawResponse: response,
  }
}

export async function savePractitionerRoomAuditors(payload = {}) {
  const response = await postSPDataWithoutParams('/api/secure/cc/support/practitioner-audit/room-auditors', payload)

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboarding || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save chamber-room auditors.'),
    rawResponse: response,
  }
}

// --- Outgoing auditor coverage (Auditing Permissions section) ---

export async function getAuditableTargets(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/practitioner-audit/auditable-targets', {
    auditorPractitionerId: params.auditorPractitionerId,
    ...(params.role ? { role: params.role } : {}),
    ...(params.search ? { search: params.search } : {}),
    page: params.page || 1,
    size: params.size || 25,
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      data: {
        targets: data.auditableTargets || [],
        auditorEventTypes: data.auditorEventTypes || [],
        auditorRoles: data.auditorRoles || [],
        selectedRole: data.selectedRole || '',
        total: data.total || 0,
        page: data.page || 1,
        size: data.size || (params.size || 25),
      },
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load auditable practitioners.'),
    rawResponse: response,
  }
}

export async function attachOutgoingAuditor(payload = {}) {
  const response = await postSPDataWithoutParams(
    '/api/secure/cc/support/practitioner-audit/outgoing-auditor/attach',
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, data: getDataMapFromCommonResponse(response) || {}, rawResponse: response }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to attach auditor to this room.'),
    rawResponse: response,
  }
}

export async function detachOutgoingAuditor(payload = {}) {
  const response = await postSPDataWithoutParams(
    '/api/secure/cc/support/practitioner-audit/outgoing-auditor/detach',
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true, data: getDataMapFromCommonResponse(response) || {}, rawResponse: response }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to detach auditor from this room.'),
    rawResponse: response,
  }
}

export async function savePractitionerOnboarding(payload = {}) {
  const response = await postSPDataWithoutParams('/api/secure/cc/support/practitioner-onboarding/save', payload)

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboarding || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save practitioner onboarding.'),
    rawResponse: response,
  }
}

export async function suggestPractitionerSlug(firstName) {
  const response = await getSPDataWithParams('/api/secure/creator/slug/suggest', {
    firstName,
  })

  if (response?.status === 200 && typeof response?.data === 'string') {
    return {
      ok: true,
      data: response.data,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: '',
    message: getResponseMessage(response, 'Unable to suggest a slug.'),
    rawResponse: response,
  }
}

// Phase 14ai — second arg is the practitioner's own id. When editing an existing record (the
// support onboarding flow always edits, /apply submissions land here as edits too), passing
// the id lets the backend ignore the practitioner's own row when answering "is this slug
// taken." Without it, the recheck flagged a /apply-submitted practitioner's own slug as
// taken on every save.
export async function checkPractitionerSlugAvailability(slug, excludeId) {
  const params = { slug }
  if (excludeId) params['exclude-id'] = excludeId
  const response = await getSPDataWithParams('/api/secure/creator/slug/check-availability', params)

  if (response?.status === 200) {
    return {
      ok: true,
      data: {
        available: Boolean(response?.data),
      },
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: {
      available: false,
    },
    message: getResponseMessage(response, 'Unable to validate the slug.'),
    rawResponse: response,
  }
}

export async function getClinicAppointmentTypes(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/clinic/clinic-appointment-type/list', {
    ...(params?.clinicBrandId ? { clinicBrandId: params.clinicBrandId } : {}),
    ...(params?.clinicLocationId ? { clinicId: params.clinicLocationId } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.appointmentTypes || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: [],
    message: getResponseMessage(response, 'Unable to load clinic appointment types.'),
    rawResponse: response,
  }
}

export async function getSupportPractitionerSettings(params = {}) {
  const response = await getSPDataWithParams('/api/secure/cc/support/practitioner-onboarding/settings', {
    ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
    ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
  })

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerSettings || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner settings.'),
    rawResponse: response,
  }
}

export async function saveSupportPractitionerSettings(params = {}, payload = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner-onboarding/settings/save?${new URLSearchParams({
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    }).toString()}`,
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerSettings || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save practitioner settings.'),
    rawResponse: response,
  }
}

export async function getSupportPractitionerPatientContactVisibility(params = {}) {
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner/patient-contact-visibility',
    {
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerPatientContactVisibility || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner patient contact visibility.'),
    rawResponse: response,
  }
}

export async function saveSupportPractitionerPatientContactVisibility(params = {}, payload = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner/patient-contact-visibility/save?${new URLSearchParams({
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    }).toString()}`,
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerPatientContactVisibility || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save practitioner patient contact visibility.'),
    rawResponse: response,
  }
}

export async function getSupportPractitionerPatientScope(params = {}) {
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner/patient-scope',
    {
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerPatientScope || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner patient scope.'),
    rawResponse: response,
  }
}

export async function saveSupportPractitionerPatientScope(params = {}, payload = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner/patient-scope/save?${new URLSearchParams({
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    }).toString()}`,
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerPatientScope || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save practitioner patient scope.'),
    rawResponse: response,
  }
}

export async function getSupportPractitionerCommunicationGates(params = {}) {
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner/communication-gates',
    {
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerCommunicationGates || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load practitioner communication gates.'),
    rawResponse: response,
  }
}

export async function saveSupportPractitionerCommunicationGates(params = {}, payload = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner/communication-gates/save?${new URLSearchParams({
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    }).toString()}`,
    payload,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerCommunicationGates || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save practitioner communication gates.'),
    rawResponse: response,
  }
}

export async function getPractitionerSpecializationCatalog(discipline) {
  const response = await getSPDataWithParams(
    '/api/secure/practitioner-onboarding/reference/specializations',
    discipline ? { discipline } : {},
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.specializationCatalog || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load specialization catalog.'),
    rawResponse: response,
  }
}

export async function getPractitionerQualificationCatalog(discipline, specializations) {
  const params = {}
  if (discipline) params.discipline = discipline
  if (Array.isArray(specializations) && specializations.length > 0) {
    params.specializations = specializations.join(',')
  }

  const response = await getSPDataWithParams(
    '/api/secure/practitioner-onboarding/reference/qualifications',
    params,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.qualificationCatalog || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load qualifications catalog.'),
    rawResponse: response,
  }
}

export async function checkPractitionerContactExistence({ email, mobile } = {}) {
  const params = {}
  if (email) params.email = email
  if (mobile) params.mobile = mobile

  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/contact/existence-check',
    params,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.contactExistenceCheck || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to check existing user records.'),
    rawResponse: response,
  }
}

export async function markPractitionerContactVerifiedOffline({ userId, channel } = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner-onboarding/contact/verify-offline?${new URLSearchParams({
      'user-id': userId || '',
      channel: channel || '',
    }).toString()}`,
    {},
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response) || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to mark contact as offline-verified.'),
    rawResponse: response,
  }
}

export async function saveSupportFeeModel(feeModel) {
  const response = await postSPDataWithoutParams(
    '/api/secure/cc/support/practitioner-onboarding/fee-model/save',
    feeModel,
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response) || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to save fee model.'),
    rawResponse: response,
  }
}

export async function approveTherapistApplicant({ practitionerId, discipline } = {}) {
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner-onboarding/applicants/approve?${new URLSearchParams({
      'practitioner-id': practitionerId || '',
      ...(discipline ? { discipline } : {}),
    }).toString()}`,
    {},
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response) || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to approve applicant.'),
    rawResponse: response,
  }
}

/**
 * Phase 14c — reject an applicant with a mandatory reason. Posts JSON body so the reason can
 * carry punctuation / newlines without query-string mangling.
 */
export async function rejectTherapistApplicant({ practitionerId, reason } = {}) {
  const trimmedReason = (reason || '').trim()
  if (!practitionerId) return { ok: false, message: 'Missing practitioner id.' }
  if (!trimmedReason) return { ok: false, message: 'Rejection reason is required.' }
  const response = await postSPDataWithoutParams(
    `/api/secure/cc/support/practitioner-onboarding/applicants/reject?${new URLSearchParams({
      'practitioner-id': practitionerId,
    }).toString()}`,
    { reason: trimmedReason },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response) || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to reject applicant.'),
    rawResponse: response,
  }
}

// `reason` (optional) is captured into the practitioner access log on the backend alongside
// the actor + timestamp. `enabled=false` disables the practitioner; `true` re-enables.
export async function updateSupportPractitionerEnabled(params = {}, enabled = true, reason = '') {
  const trimmedReason = (reason || '').trim()
  const response = await patchSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/enabled',
    {
      enabled,
      ...(trimmedReason ? { reason: trimmedReason } : {}),
    },
    {
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.practitionerOnboarding || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to update practitioner status.'),
    rawResponse: response,
  }
}

// Per-practitioner enable/disable access-log trail (newest first).
export async function getSupportPractitionerAccessLog(params = {}) {
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/access-log',
    {
      ...(params?.practitionerId ? { 'practitioner-id': params.practitionerId } : {}),
      ...(params?.practitionerSlug ? { 'practitioner-slug': params.practitionerSlug } : {}),
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.accessLogs || [],
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: [],
    message: getResponseMessage(response, 'Unable to load practitioner access log.'),
    rawResponse: response,
  }
}

// Platform-wide enable/disable access-log trail. Optional `type` (therapist/doctor/screener)
// and `action` (ENABLED/DISABLED) filters; paginated via `page` / `size`.
export async function getAllSupportPractitionerAccessLogs(params = {}) {
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/access-log/all',
    {
      ...(params?.type ? { type: params.type } : {}),
      ...(params?.action ? { action: params.action } : {}),
      page: Number.isFinite(params?.page) ? params.page : 0,
      size: Number.isFinite(params?.size) ? params.size : 50,
    },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response)?.practitionerAccessLogs || {}
    return {
      ok: true,
      data: {
        logs: Array.isArray(data?.logs) ? data.logs : [],
        page: Number.isFinite(data?.page) ? data.page : 0,
        size: Number.isFinite(data?.size) ? data.size : 50,
        total: Number.isFinite(data?.total) ? data.total : 0,
      },
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: { logs: [], page: 0, size: 50, total: 0 },
    message: getResponseMessage(response, 'Unable to load practitioner access logs.'),
    rawResponse: response,
  }
}

/**
 * Phase 14al Slice 3 — pulls the practitioner-role → allowed-event-types policy. Called
 * once on the support onboarding page bootstrap; the chamber-room status pill in the new
 * brand-grouped Clinic Assignment IA reads from the returned map. Falls back to a hardcoded
 * frontend constant when the call fails so the SPA keeps rendering even if the API is down.
 */
export async function getRoleEventTypesPolicy(practitionerType) {
  const params = practitionerType ? { type: practitionerType } : {}
  const response = await getSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/role-event-types',
    params,
  )
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      data: practitionerType
        ? {
            practitionerType: data.practitionerType || practitionerType,
            allowedEventTypes: Array.isArray(data.allowedEventTypes) ? data.allowedEventTypes : [],
          }
        : { policies: data.policies && typeof data.policies === 'object' ? data.policies : {} },
      rawResponse: response,
    }
  }
  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to load role-event-types policy.'),
    rawResponse: response,
  }
}

/**
 * Phase 14ai — flip the applicant's banking-verified flag (with optional revoke when
 * verified=false). Body carries free-form notes the support reviewer captures along with
 * the decision; backend stamps verifiedBy + verifiedAt audit fields.
 */
export async function markApplicantBankingVerified({ practitionerId, verified = true, notes = '' }) {
  const response = await postSPDataWithParams(
    '/api/secure/cc/support/practitioner-onboarding/banking/mark-verified',
    { verified, notes: notes || '' },
    { 'practitioner-id': practitionerId },
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return {
      ok: true,
      data: getDataMapFromCommonResponse(response)?.banking || null,
      rawResponse: response,
    }
  }

  return {
    ok: false,
    data: null,
    message: getResponseMessage(response, 'Unable to update banking verification.'),
    rawResponse: response,
  }
}
